import { applySnapshot, getEnv, Instance, SnapshotOut, types } from 'mobx-state-tree'
import { clearPushIdentity, getPushAnonymousId } from '@/modules/push'
import { getSegmentClient, setSegmentCioAnonymousId } from '@/models/analytics/segment'

import { emitter, LogoutEvent } from '../../utils/events'
import { events } from '../analytics/analytics-events'
import { JwtModel, JwtModelType } from '../api-store'
import { withRootStore } from '../extensions'
import { RootStoreEnv } from '../RootStoreEnv'
import { useRemoteConfig } from '../../../module/remote-config/useRemoteConfig'
import { RemoteConfigSchema } from '../../../module/remote-config/remoteConfigSchema'
import remoteConfig from '@react-native-firebase/remote-config'
import { initRemoteConfig } from '../../../module/remote-config/useInitRemoteConfig'
import { Platform } from 'react-native'
import { auth0Config } from '@/config/auth0'
import * as WebBrowser from 'expo-web-browser'
import { useSessionStore } from './useSessionStore'
import { useMessageStore } from '../../hooks/convex/useMessageStore'
import { useInitialLinkingStore } from '../../hooks/useInitialLinkingStore'
import { useAvatarStore } from '../../screens/profile-screen/edit/AvatarModule'
import { useActionAdvisorStore } from '../../screens/GameContainer/useActionAdvisorStore'
WebBrowser.maybeCompleteAuthSession()

/**
 * Segment's reset() and Customer.io's clearIdentify() each create a new anonymous ID.
 * Run them in order, then give Segment Customer.io's new ID. Web has no Customer.io SDK.
 */
async function resetAnalyticsIdentity() {
  // Stop replacing IDs with the old Customer.io ID while both reset.
  await setSegmentCioAnonymousId(null)
  await getSegmentClient()?.reset()
  if (Platform.OS === 'web') return
  await clearPushIdentity()
  await setSegmentCioAnonymousId(await getPushAnonymousId())
}

/**
 * A SessionTokenStore model.
 */
export const SessionTokenStoreModel = types
  .model('SessionTokenStoreModel')
  .props({
    isBootstrapped: types.optional(types.boolean, false),

    wordpressToken: types.maybe(types.string),
    wordpressTokenExpiredTimestamp: types.maybe(types.number)
  })
  .extend(withRootStore)
  .actions((self) => ({
    isWordpressTokenExpired() {
      const currentTimestamp = Math.floor(Date.now() / 1000)
      const isExpired = (self.wordpressTokenExpiredTimestamp ?? 0) < currentTimestamp
      return isExpired
    }
  }))
  .actions((self) => ({
    setWordpressToken(token?: string) {
      self.wordpressToken = token
    },
    setWordpressTokenExpireTimestamp(value: number) {
      self.wordpressTokenExpiredTimestamp = value
    }
  }))

  .actions((self) => {
    const logout = async (config?: { analytics: boolean }) => {
      if (config?.analytics) {
        self.rootStore.analytics.setEvent(events.logout.user_logout)
      }

      // On web, navigate to universal logout screen which handles cross-platform logout
      if (Platform.OS === 'web') {
        const returnTo = encodeURIComponent(`${window.location.origin}/login`)
        // Use expo-router to navigate instead of window.location
        const { router } = require('expo-router')
        router.push(`/universal-logout?returnTo=${returnTo}`)
        return
      }

      // Native app logout (unchanged)
      const savedAnonymousUserId = self.rootStore.share.segmentAnonymousId
      console.log('logout')
      self.rootStore.game.teardown()

      useSessionStore.getState().clearTokenConfig()
      self.setWordpressToken(undefined)

      // Clear message store timestamps on logout
      useMessageStore.getState().clearAll()

      // Reset advisor preferences so the next login doesn't inherit this
      // account's advisor settings (enabled switch, coaching level, mute,
      // welcome budget) — the persisted record is device-global.
      useActionAdvisorStore.getState().resetUserSettings()

      // Reset avatar preferences so the next login doesn't inherit this user's avatar
      useAvatarStore.getState().init()

      useRemoteConfig.setState(RemoteConfigSchema.parse({}))
      if (Platform.OS === 'android') {
        remoteConfig().reset()
      }
      initRemoteConfig()

      applySnapshot(self.rootStore, {
        environment: self.rootStore.environment
      })
      void resetAnalyticsIdentity()
      //NOTE: Save anonymous user id if user install and kills the app without login
      if (savedAnonymousUserId) {
        self.rootStore.share.restoreSegmentAnonymousId(savedAnonymousUserId)
      }

      useInitialLinkingStore.getState().dispose('all')
    }
    const login = async (email: string, password: string) => {
      try {
        console.log('login', { email, password })
        // ... yield can be used in async/await style
        // const response: AuthenticationResponse = await root.services.api.session.post('/auth/login', {
        //   email,
        //   password,
        // })
        // const { accessToken } = response.data
        // console.log('logged in', { email, password, accessToken })
        // if (accessToken) {
        //   self.setToken(accessToken)
        // }
      } catch (error) {
        // ... including try/catch error handling
        console.error('unable to login', { error })
        logout()
      }
      const accessToken = useSessionStore.getState().getAccessToken()
      console.log('JWT', { token: accessToken })
      // The action will return a promise that resolves to the returned value
      // (or rejects with anything thrown from the action)
      return accessToken
    }

    const cleanupSession = () => {
      // This is all the cleanup from logout() but WITHOUT the Auth0 redirect
      // Used by universal logout screen which handles Auth0 logout separately
      const savedAnonymousUserId = self.rootStore.share.segmentAnonymousId
      self.rootStore.game.teardown()

      useSessionStore.getState().clearTokenConfig()
      self.setWordpressToken(undefined)

      // Clear message store timestamps on logout
      useMessageStore.getState().clearAll()

      // Reset advisor preferences so the next login doesn't inherit this
      // account's advisor settings (enabled switch, coaching level, mute,
      // welcome budget) — the persisted record is device-global.
      useActionAdvisorStore.getState().resetUserSettings()

      // Reset avatar preferences so the next login doesn't inherit this user's avatar
      useAvatarStore.getState().init()

      useRemoteConfig.setState(RemoteConfigSchema.parse({}))
      if (Platform.OS === 'android') {
        remoteConfig().reset()
      }
      initRemoteConfig()

      applySnapshot(self.rootStore, {
        environment: self.rootStore.environment
      })

      void resetAnalyticsIdentity()
      if (savedAnonymousUserId) {
        self.rootStore.share.restoreSegmentAnonymousId(savedAnonymousUserId)
      }

      useInitialLinkingStore.getState().dispose('all')
    }

    return { login, logout, cleanupSession }
  })
  .actions((self) => {
    const afterCreate = () => {
      self.isBootstrapped = false
      emitter.addListener(LogoutEvent, self.logout)
      self.isBootstrapped = true
    }
    const beforeDestroy = () => {
      emitter.removeListener(LogoutEvent, self.logout)
      return
    }
    return { afterCreate, beforeDestroy }
  })

/**
 * The SessionTokenStore instance.
 */
export type SessionTokenStore = Instance<typeof SessionTokenStoreModel>

/**
 * The data of a SessionTokenStore.
 */
export type SessionTokenStoreSnapshot = SnapshotOut<typeof SessionTokenStoreModel>
